import { motion } from 'framer-motion';
import { ArrowRight, Mail, MapPin, Globe, Mic, GraduationCap, Award, Users, Calendar, Star, Linkedin, Twitter, Github, ExternalLink, MessageCircle, Code, ExternalLink as ExternalLinkIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { personalData } from '../data/personal';
import { conferencesData } from '../data/conferences';
import { projectsData } from '../data/projects';
import { useState, useEffect, useRef } from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import React from 'react';

const HomePage = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [activeTimelineItem, setActiveTimelineItem] = useState(0);

  const titles = [
    "Technical AI Product Leader",
    "Computer Science Adjunct Faculty Member", 
    "Licensed FAA Pilot",
    "Licensed California Real Estate Agent",
    "Day Trader and Investor  "
  ];

  useEffect(() => {
    const typeSpeed = 100;
    const pauseTime = 3000; // Pause at the end of each title

    const typeText = () => {
      const currentTitle = titles[currentTextIndex];
      
      if (isTyping) {
        setCurrentText(currentTitle.substring(0, currentText.length + 1));
        if (currentText === currentTitle) {
          // Finished typing current title, pause then move to next
          setTimeout(() => {
            setIsTyping(false);
            setCurrentTextIndex((prev) => (prev + 1) % titles.length);
            setCurrentText('');
            setIsTyping(true);
          }, pauseTime);
        }
      }
    };

    const timer = setTimeout(typeText, typeSpeed);
    return () => clearTimeout(timer);
  }, [currentText, isTyping, currentTextIndex, titles]);

  // Auto-rotate timeline
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimelineItem((prev) => (prev + 1) % personalData.education.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Filter for specific featured projects to showcase
  const featuredProjects = projectsData.filter(project =>
    project.id === "observability-zero-to-hero" ||
    project.id === "neural-networks" ||
    project.id === "fintellect-ai" ||
    project.id === "walmart-forecasting" ||
    project.id === "rl-workbench" ||
    project.id === "authz-platform" ||
    project.id === "brain-2026" ||
    project.id === "ai-eval" ||
    project.id === "option-chain-analyzer" ||
    project.id === "agentops" ||
    project.id === "rag-system" ||
    project.id === "fine-tuned-model" ||
    project.id === "agentic-workflow" ||
    project.id === "inference-pipeline" ||
    project.id === "ml-monitoring"
  );

  // Keen-slider setup with autoplay, rubberbanding, custom cursor
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    rubberband: true,
    drag: true,
    mode: 'free-snap',
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: { perView: 2, spacing: 24 },
      },
    },
    dragStarted: () => setIsDragging(true),
    dragEnded: () => setIsDragging(false),
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
  });

  // Autoplay effect
  useEffect(() => {
    if (!instanceRef.current) return;
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      instanceRef.current?.next();
    }, 4000);
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [instanceRef]);

  // Language Skills Carousel
  const [langCurrentSlide, setLangCurrentSlide] = useState(0);
  const [langIsDragging, setLangIsDragging] = useState(false);
  const langAutoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [langSliderRef, langInstanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    rubberband: true,
    drag: true,
    mode: 'free-snap',
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: { perView: 2, spacing: 24 },
      },
    },
    dragStarted: () => setLangIsDragging(true),
    dragEnded: () => setLangIsDragging(false),
    slideChanged(s) {
      setLangCurrentSlide(s.track.details.rel);
    },
  });

  useEffect(() => {
    if (!langInstanceRef.current) return;
    if (langAutoplayRef.current) clearInterval(langAutoplayRef.current);
    langAutoplayRef.current = setInterval(() => {
      langInstanceRef.current?.next();
    }, 4000);
    return () => {
      if (langAutoplayRef.current) clearInterval(langAutoplayRef.current);
    };
  }, [langInstanceRef]);

  // Certifications Carousel
  const [certCurrentSlide, setCertCurrentSlide] = useState(0);
  const [certIsDragging, setCertIsDragging] = useState(false);
  const certAutoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [certSliderRef, certInstanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    rubberband: true,
    drag: true,
    mode: 'free-snap',
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: { perView: 2, spacing: 24 },
      },
    },
    dragStarted: () => setCertIsDragging(true),
    dragEnded: () => setCertIsDragging(false),
    slideChanged(s) {
      setCertCurrentSlide(s.track.details.rel);
    },
  });

  useEffect(() => {
    if (!certInstanceRef.current) return;
    if (certAutoplayRef.current) clearInterval(certAutoplayRef.current);
    certAutoplayRef.current = setInterval(() => {
      certInstanceRef.current?.next();
    }, 4000);
    return () => {
      if (certAutoplayRef.current) clearInterval(certAutoplayRef.current);
    };
  }, [certInstanceRef]);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Enhanced Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-20"
      >
        {/* Profile Image with Enhanced Styling */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto mb-8"
        >
          <div className="absolute -inset-3 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full blur-xl opacity-20 animate-pulse -z-10"></div>
          <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-primary-200 dark:border-primary-800 shadow-2xl">
            <img
              src="/assets/images/headshot_512.jpg"
              alt="Felix Amoruwa"
              className="w-full h-full object-cover object-top"
              style={{ imageRendering: 'auto' }}
              loading="eager"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-4xl font-bold">FA</div>';
              }}
            />
          </div>
          {/* Status Indicator */}
          <div className="absolute -bottom-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full border-4 border-background flex items-center justify-center">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full animate-pulse"></div>
          </div>
        </motion.div>

        {/* Enhanced Name */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-3xl sm:text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-foreground to-primary-600 bg-clip-text text-transparent"
        >
          {personalData.name}
        </motion.h1>

        {/* Typing Effect for Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-4 h-8 flex items-center justify-center"
        >
          <span className="mr-2">I'm a</span>
          <span className="text-primary-600 font-semibold min-w-[200px] sm:min-w-[300px] md:min-w-[400px] text-left">
            {currentText}
            <span className="animate-pulse">|</span>
          </span>
        </motion.div>

        {/* Enhanced Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 text-sm sm:text-base"
        >
          <div className="flex items-center gap-2 text-muted-foreground hover:text-primary-600 transition-colors group">
            <Mail size={16} className="group-hover:scale-110 transition-transform flex-shrink-0" />
            <a 
              href={`mailto:${personalData.email}`}
              className="hover:text-primary-600 transition-colors break-all"
            >
              {personalData.email}
            </a>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin size={16} className="flex-shrink-0" />
            <span className="text-center sm:text-left">{personalData.location}</span>
          </div>
          {personalData.website && (
            <div className="flex items-center gap-2 text-muted-foreground hover:text-primary-600 transition-colors group">
              <Globe size={16} className="group-hover:scale-110 transition-transform flex-shrink-0" />
              <a 
                href={personalData.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-600 transition-colors break-all"
              >
                {personalData.website}
              </a>
            </div>
          )}
        </motion.div>

        {/* Social Media Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex justify-center gap-4 mb-8"
        >
          {personalData.social.map((social, index) => (
            <motion.a
              key={social.platform}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.0 + index * 0.1 }}
              className="w-12 h-12 bg-secondary-100 dark:bg-secondary-800 rounded-full flex items-center justify-center hover:bg-primary-100 dark:hover:bg-primary-900 hover:scale-110 transition-all duration-300 group"
            >
              {social.platform === 'LinkedIn' && <Linkedin size={20} className="text-muted-foreground group-hover:text-primary-600 transition-colors" />}
              {social.platform === 'Twitter' && <Twitter size={20} className="text-muted-foreground group-hover:text-primary-600 transition-colors" />}
              {social.platform === 'GitHub' && <Github size={20} className="text-muted-foreground group-hover:text-primary-600 transition-colors" />}
              {social.platform === 'Hacker News' && <ExternalLink size={20} className="text-muted-foreground group-hover:text-primary-600 transition-colors" />}
            </motion.a>
          ))}
        </motion.div>

        {/* Enhanced CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center"
        >
          <Link
            to="/portfolio"
            className="group btn btn-primary inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <span>View My Work</span>
            <ArrowRight size={18} className="sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/contact"
            className="group btn btn-outline px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold hover:bg-primary-50 dark:hover:bg-primary-950 hover:scale-105 transition-all duration-300 border-2"
          >
            <MessageCircle size={18} className="sm:w-5 sm:h-5 mr-2 group-hover:scale-110 transition-transform" />
            Get In Touch
          </Link>
        </motion.div>
      </motion.div>

      {/* About Section with Headline */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="max-w-4xl mx-auto mb-20"
      >
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.3 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full mb-4"
          >
            <Users size={24} className="text-primary-600 dark:text-primary-400" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4">About Me</h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full mb-4"></div>
          {/* Impactful Headline */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            className="text-xl md:text-2xl font-semibold text-primary-600 mb-6"
          >
            Building intelligent platforms at the intersection of AI, data, and product.
          </motion.p>
        </div>
        <div className="prose prose-lg dark:prose-invert mx-auto">
          <p className="text-muted-foreground leading-relaxed text-lg">
            {personalData.bio.split('\n').map((line, idx, arr) => {
              if (line.trim() === '') {
                // Only render a <br /> for a blank line if the previous line was not blank
                if (idx > 0 && arr[idx - 1].trim() !== '') {
                  return <br key={idx} />;
                }
                return null;
              }
              return (
                <React.Fragment key={idx}>
                  {line}
                  <br />
                </React.Fragment>
              );
            })}
          </p>
        </div>
      </motion.div>

      {/* Interactive Timeline for Education */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="max-w-6xl mx-auto mb-20"
      >
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.5 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full mb-4"
          >
            <GraduationCap size={24} className="text-green-600 dark:text-green-400" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4">Education Timeline</h2>
          <div className="w-24 h-1 bg-green-600 mx-auto rounded-full"></div>
        </div>
        
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-green-200 dark:bg-green-800"></div>
          
          <div className="space-y-8">
            {personalData.education.map((edu, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.6 + index * 0.2 }}
                className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
              >
                {/* Timeline Dot */}
                <div className={`absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-4 border-background ${
                  activeTimelineItem === index ? 'bg-green-500 animate-pulse' : 'bg-green-300 dark:bg-green-700'
                } transition-all duration-300`}></div>
                
                {/* Content Card */}
                <motion.div
                  className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActiveTimelineItem(index)}
                >
                  <div className={`card cursor-pointer transition-all duration-300 ${
                    activeTimelineItem === index 
                      ? 'shadow-lg border-green-300 dark:border-green-700' 
                      : 'hover:shadow-md'
                  }`}>
                    <div className="card-content p-6">
                      <div className="flex items-center gap-4">
                        {edu.image && (
                          <img
                            src={edu.image}
                            alt={edu.institution}
                            className="w-16 h-16 object-contain rounded-lg border bg-white p-2"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{edu.degree}</h3>
                          <p className="text-muted-foreground mb-2">{edu.institution}</p>
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-green-600" />
                            <span className="text-sm text-muted-foreground">{edu.year}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Animated Language Skills Carousel */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
        className="max-w-4xl mx-auto mb-20"
      >
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.7 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4"
          >
            <Users size={24} className="text-blue-600 dark:text-blue-400" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4">Language Skills</h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </div>
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            aria-label="Previous language"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
            onClick={() => langInstanceRef.current?.prev()}
            style={{ left: '-2.5rem' }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            aria-label="Next language"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
            onClick={() => langInstanceRef.current?.next()}
            style={{ right: '-2.5rem' }}
          >
            <ChevronRight size={24} />
          </button>
          {/* Carousel */}
          <div
            ref={langSliderRef}
            className={`keen-slider ${langIsDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ userSelect: langIsDragging ? 'none' : 'auto' }}
          >
            {personalData.languages.map((language, index) => (
              <div className="keen-slider__slide p-2" key={language.name}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.8 + index * 0.1 }}
                  className="card hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="card-content p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-lg">{language.name}</h3>
                      <span className="text-sm text-muted-foreground capitalize">{language.level}</span>
                    </div>
                    {/* Animated Progress Bar */}
                    <div className="relative">
                      <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3">
                        <motion.div
                          className={`h-3 rounded-full shadow-lg relative overflow-visible`}
                          initial={{ width: 0 }}
                          animate={{ width: `${(language.stars / 5) * 100}%` }}
                          transition={{ duration: 1.5, delay: 2.0 + index * 0.2, ease: 'easeOut' }}
                          style={{
                            background: 'linear-gradient(90deg, #38bdf8 0%, #0ea5e9 70%, #0369a1 100%)',
                            boxShadow: '0 2px 8px 0 rgba(14,165,233,0.18), 0 1px 0 0 #fff6 inset'
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: '100%',
                              height: '60%',
                              borderRadius: '8px',
                              background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.05) 100%)',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}
                          />
                        </motion.div>
                      </div>
                      {/* Stars */}
                      <div className="flex gap-1 mt-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={16}
                            className={`${
                              star <= language.stars 
                                ? 'text-yellow-500 fill-current' 
                                : 'text-secondary-300 dark:text-secondary-600'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {personalData.languages.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Go to language slide ${idx + 1}`}
                className={`w-3 h-3 rounded-full border-2 ${langCurrentSlide === idx ? 'bg-blue-600 border-blue-600' : 'bg-white dark:bg-background border-blue-300 dark:border-blue-700'} transition-all`}
                onClick={() => langInstanceRef.current?.moveToIdx(idx)}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Featured Projects Carousel */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        className="max-w-6xl mx-auto mb-20"
      >
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.9 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full"
            >
              <Code size={24} className="text-orange-600 dark:text-orange-400" />
            </motion.div>
            <div>
              <h2 className="text-4xl font-bold">AI Featured Projects</h2>
              <div className="w-24 h-1 bg-orange-600 rounded-full mt-2"></div>
            </div>
          </div>
          <Link
            to="/portfolio"
            className="group text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2 hover:scale-105 transition-transform"
          >
            View All Projects
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            aria-label="Previous project"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-orange-100 dark:hover:bg-orange-900 transition-all"
            onClick={() => instanceRef.current?.prev()}
            style={{ left: '-2.5rem' }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            aria-label="Next project"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-orange-100 dark:hover:bg-orange-900 transition-all"
            onClick={() => instanceRef.current?.next()}
            style={{ right: '-2.5rem' }}
          >
            <ChevronRight size={24} />
          </button>
          {/* Carousel */}
          <div
            ref={sliderRef}
            className={`keen-slider ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ userSelect: isDragging ? 'none' : 'auto' }}
          >
            {featuredProjects.map((project, index) => (
              <div className="keen-slider__slide p-2" key={project.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 2.0 + index * 0.1 }}
                  className="group card hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-orange-200 dark:hover:border-orange-800 h-full flex flex-col"
                >
                  <div className="h-48 bg-secondary-100 dark:bg-secondary-800 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class=\"w-full h-full flex items-center justify-center\"><span class=\"text-4xl font-bold text-secondary-400\">FA</span></div>';
                      }}
                    />
                  </div>
                  <div className="card-content p-6 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-lg group-hover:text-orange-600 transition-colors line-clamp-1">
                        {project.title}
                      </h3>
                      <span className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs px-2 py-1 rounded-full">
                        Featured
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2 flex-1">
                      {project.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-sm text-muted-foreground">
                        {project.year} • {project.category}
                      </span>
                      <div className="flex gap-2">
                        {project.link && (
                          <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 transition-colors"
                          >
                            <ExternalLinkIcon size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {featuredProjects.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Go to slide ${idx + 1}`}
                className={`w-3 h-3 rounded-full border-2 ${currentSlide === idx ? 'bg-orange-600 border-orange-600' : 'bg-white dark:bg-background border-orange-300 dark:border-orange-700'} transition-all`}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Certifications Carousel */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.0 }}
        className="max-w-6xl mx-auto mb-20"
      >
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 2.1 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4"
          >
            <Award size={24} className="text-blue-600 dark:text-blue-400" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4">Certifications</h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </div>
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            aria-label="Previous certification"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
            onClick={() => certInstanceRef.current?.prev()}
            style={{ left: '-2.5rem' }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            aria-label="Next certification"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
            onClick={() => certInstanceRef.current?.next()}
            style={{ right: '-2.5rem' }}
          >
            <ChevronRight size={24} />
          </button>
          {/* Carousel */}
          <div
            ref={certSliderRef}
            className={`keen-slider ${certIsDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ userSelect: certIsDragging ? 'none' : 'auto' }}
          >
            {personalData.certifications.map((cert, index) => (
              <div className="keen-slider__slide p-2" key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 2.2 + index * 0.1 }}
                  className="group card hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 hover:border-blue-200 dark:hover:border-blue-800"
                >
                  <div className="card-content text-center p-6 flex flex-col items-center">
                    <img
                      src="/assets/images/faa.png"
                      alt="FAA Logo"
                      className="w-16 h-16 object-contain rounded-full border bg-white mb-4 shadow"
                    />
                    <h3 className="font-semibold mb-3 text-lg group-hover:text-blue-600 transition-colors">{cert.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{cert.issuer}</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        cert.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                          : cert.status === 'scheduled'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {cert.status}
                      </span>
                      <span className="text-xs text-muted-foreground">{cert.year}</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {personalData.certifications.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Go to certification slide ${idx + 1}`}
                className={`w-3 h-3 rounded-full border-2 ${certCurrentSlide === idx ? 'bg-blue-600 border-blue-600' : 'bg-white dark:bg-background border-blue-300 dark:border-blue-700'} transition-all`}
                onClick={() => certInstanceRef.current?.moveToIdx(idx)}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Enhanced Conferences Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.1 }}
        className="max-w-6xl mx-auto mb-20"
      >
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 2.2 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full"
            >
              <Mic size={24} className="text-purple-600 dark:text-purple-400" />
            </motion.div>
            <div>
              <h2 className="text-4xl font-bold">Conferences & Speaking</h2>
              <div className="w-24 h-1 bg-purple-600 rounded-full mt-2"></div>
            </div>
          </div>
          <Link
            to="/conferences"
            className="group text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2 hover:scale-105 transition-transform"
          >
            View All
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {conferencesData.slice(0, 2).map((conference, index) => (
            <motion.div
              key={conference.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 2.3 + index * 0.1 }}
              className="group card hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 hover:border-purple-200 dark:hover:border-purple-800"
            >
              <div className="card-content p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Mic size={16} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm font-medium text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
                    {conference.role}
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-3 group-hover:text-purple-600 transition-colors">{conference.name}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{conference.location}, {conference.year}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{conference.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage; 